import { instance as sinatra } from '@rsos/sinatra';
import { captureException } from '@rsos/utils/sentry';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import type {
  SSOStatusResponse,
  SSOSetupResponse,
  SSOConfigurationValues,
  IDPConfigurationValues,
  SSOSetupError,
  SSOConfigError,
  SSOResetError,
} from '../types/sso';

class SSOSetupErrorImpl extends Error implements SSOSetupError {
  name = 'SSOSetupError';

  constructor(message: string) {
    super(message);
  }
}

class SSOConfigErrorImpl extends Error implements SSOConfigError {
  name = 'SSOConfigError';

  constructor(message: string) {
    super(message);
  }
}

class SSOResetErrorImpl extends Error implements SSOResetError {
  name = 'SSOResetError';

  constructor(message: string) {
    super(message);
  }
}

export interface SSOStatusData {
  domain?: string;
  ssoEnabled?: boolean;
  selectedRole?: string;
  ssoGroupAttribute?: string; // optional, required for backwards compatibility
  jitEnabled?: boolean;
}

export class SSOService {
  async getSSOStatus(
    orgId: string,
    currentApp: string,
  ): Promise<SSOStatusData> {
    try {
      const response = await sinatra.get<SSOStatusResponse>(
        `/sso/activate/${orgId}/${currentApp}`,
      );

      return {
        domain: response?.data?.domain,
        ssoEnabled: response?.data?.sso_enabled || false,
        selectedRole: response?.data?.default_role || '',
        ssoGroupAttribute: response?.data?.sso_group_attribute || '',
        jitEnabled: response?.data?.jit_enabled || false,
      };
    } catch (error) {
      captureException(new SSOSetupErrorImpl('Failed to get sso settings'), {
        orgId,
        currentApp,
        error,
      });
      throw error;
    }
  }

  async setupIdentityProvider(
    orgId: string,
    values: IDPConfigurationValues,
  ): Promise<void> {
    try {
      const resp = await sinatra.post<SSOSetupResponse>(
        `/sso/setup_link/${orgId}`,
        {
          organization_domain: values.domain,
        },
      );

      const setupURI = resp.data?.setup_link;
      if (setupURI) {
        window.open(setupURI, '_blank', 'noreferrer');
      } else {
        throw new SSOSetupErrorImpl('Failed to get setup link');
      }
    } catch (error) {
      captureException(new SSOSetupErrorImpl('Failed to setup IDP'), { error });
      throw error;
    }
  }

  async configureSSOSettings(
    orgId: string,
    currentApp: string,
    values: SSOConfigurationValues,
  ): Promise<{
    ssoEnabled?: boolean;
    selectedRole?: string;
    ssoGroupAttribute?: string;
    jitEnabled?: boolean;
  }> {
    try {
      const payload: {
        jit_enabled: boolean;
        sso_enabled: boolean;
        default_role: string;
        sso_group_attribute?: string;
      } = {
        jit_enabled: values.jitEnabled,
        sso_enabled: values.ssoEnabled,
        default_role: values.selectedRole,
      };

      if (values.ssoGroupAttribute && values.ssoGroupAttribute.trim() !== '') {
        payload.sso_group_attribute = values.ssoGroupAttribute;
      }

      const response = await sinatra.post(
        `/sso/activate/${orgId}/${currentApp}`,
        payload,
      );

      // Use server response values instead of input values to ensure UI consistency
      const result = {
        ssoEnabled: response.data?.sso_enabled ?? values.ssoEnabled,
        selectedRole: values.selectedRole, // Server doesn't return default_role in POST response
        ssoGroupAttribute:
          response.data?.sso_group_attribute ?? values.ssoGroupAttribute,
        jitEnabled: response.data?.jit_enabled ?? values.jitEnabled,
      };

      notifySuccess('SSO settings saved');
      return result;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : (error as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail || 'Error saving SSO settings';

      notifyError(errorMessage);
      captureException(new SSOConfigErrorImpl('Failed to save SSO settings'), {
        error,
      });
      throw error;
    }
  }

  async resetSSOConfiguration(orgId: string): Promise<void> {
    try {
      await sinatra.post(`/sso/reset/${orgId}`);
      notifySuccess('SSO Settings Reset Successfully');
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : (error as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail || 'Failed to reset SSO settings';

      notifyError(errorMessage);
      captureException(new SSOResetErrorImpl('Failed to reset SSO settings'), {
        error,
      });
      throw error;
    }
  }
}

export const ssoService = new SSOService();
